import { BACKEND_BREAKPOINT_TO_MEDIA } from './breakpoints';
import { TYPE_COLOR, TYPE_SIZE } from './types';

const CANVAS_STYLE_ID = 'sio-css-variables';

const extractVariableData = (item) => item?.['0'] ?? item;

const formatColorValue = (value) => {
    if (!value || typeof value !== 'string') return null;
    return value;
};

const formatSizeValue = (value) => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return `${value}px`;
    const { size, sizeUnit } = value;
    if (size == null) return null;
    return `${size}${sizeUnit || 'px'}`;
};

const formatValueForType = (type, rawValue) => {
    switch (type) {
        case TYPE_COLOR:
            return formatColorValue(rawValue);
        case TYPE_SIZE:
            return formatSizeValue(rawValue);
        default:
            return null;
    }
};

const buildVariablesCss = (rulesByMedia) => {
    if (!rulesByMedia?.size) return '';

    const parts = [];

    for (const [widthMedia, vars] of rulesByMedia) {
        const declarations = Object.entries(vars)
            .map(([key, value]) => `  ${key}: ${value}!important;`)
            .join('\n');

        if (!declarations) continue;

        if (!widthMedia) {
            parts.push(`:root {\n${declarations}\n}`);
        } else {
            parts.push(
                `@media (max-width: ${widthMedia}) {\n  :root {\n${declarations}\n  }\n}`
            );
        }
    }

    return parts.join('\n\n');
};

const upsertCanvasStyle = (doc, css) => {
    if (!doc?.head) return;
    let styleEl = doc.getElementById(CANVAS_STYLE_ID);
    if (!css) {
        styleEl?.remove();
        return;
    }
    if (!styleEl) {
        styleEl = doc.createElement('style');
        styleEl.id = CANVAS_STYLE_ID;
        doc.head.appendChild(styleEl);
    }
    if (styleEl.innerHTML !== css) {
        styleEl.innerHTML = css;
    }
};

export const injectVariablesToCanvas = (editor) => {
    const store = editor.getModel().get('cssVarStore');
    if (!store) return;

    const css = buildVariablesCss(store.rulesByMedia);

    try {
        const canvasDoc = editor.Canvas.getDocument();
        upsertCanvasStyle(canvasDoc, css);
    } catch {
        // Canvas not ready yet.
    }
};

/**
 * Parse backend API data and inject :root variables into the canvas only.
 */
export const applyBackendVariables = (editor, apiData) => {
    if (!apiData || !Array.isArray(apiData)) return;

    const order = [];
    const baseValues = {};
    const rulesByMedia = new Map();

    for (const item of apiData) {
        const data = extractVariableData(item);
        const type = item?.type;
        const name = data?.name;

        if (!name || !type || type === 'font-family') continue;
        if (type !== TYPE_COLOR && type !== TYPE_SIZE) continue;

        order.push({ type, name });

        for (const [backendKey, widthMedia] of Object.entries(
            BACKEND_BREAKPOINT_TO_MEDIA
        )) {
            const rawValue = data[backendKey];
            const value = formatValueForType(type, rawValue);
            if (!value) continue;

            if (!rulesByMedia.has(widthMedia)) {
                rulesByMedia.set(widthMedia, {});
            }
            rulesByMedia.get(widthMedia)[`--${name}`] = value;

            if (!widthMedia) {
                baseValues[name] = value;
            }
        }
    }

    editor.getModel().set('cssVarStore', { order, baseValues, rulesByMedia });
    injectVariablesToCanvas(editor);
};
